import React from 'react';
import { Avatar, Box, Button, ButtonBase, Card, CardContent, Chip, Skeleton, Stack, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { IOrganiserDiscovery } from '../../../interfaces/IOrganiserDiscovery';
import { getSafeHexColour } from '../../../utils/colourUtils';

const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');

const defaultSubtitle = (organisation: IOrganiserDiscovery, includeEventCount: boolean) => (
    <>
        {includeEventCount && <>{organisation.eventCount} event{organisation.eventCount === 1 ? '' : 's'} · </>}
        {organisation.communityCount} communit{organisation.communityCount === 1 ? 'y' : 'ies'}
        {organisation.nextEventStartDate
            ? ` · Next event ${new Date(organisation.nextEventStartDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
            : ' · No upcoming events'}
    </>
);

export interface OrganisationRowProps {
    organisation: IOrganiserDiscovery;
    onClick?: () => void;
    /** Overrides the default event/community-count caption, e.g. "3 events in feed". */
    subtitle?: React.ReactNode;
    /** Overrides the default "View page" button, e.g. with a follow toggle. */
    action?: React.ReactNode;
    /** 'row' for a horizontal list item, 'card' for a grid tile, 'compact' for a small avatar+name row (e.g. organiser badges). Defaults to 'card'. */
    variant?: 'row' | 'card' | 'compact';
    /** 'compact' variant only — avatar size in px. */
    avatarSize?: number;
    sx?: SxProps<Theme>;
}

/** Shared organiser summary — one implementation for the row (lists/sidebars), card (grids), and compact (small badges) layouts. */
export const OrganisationCard: React.FC<OrganisationRowProps> = ({ organisation, onClick, subtitle, action, variant = 'card', avatarSize = 38, sx }) => {
    const theme = useTheme();
    const ownerPrimary = getSafeHexColour(organisation.primaryColour, theme.palette.primary.main);
    const ownerSecondary = getSafeHexColour(organisation.secondaryColour, theme.palette.secondary.main);
    const brandGradient = `linear-gradient(135deg, ${ownerPrimary}, ${ownerSecondary})`;

    if (variant === 'compact') {
        const avatar = (
            <Avatar
                variant="rounded"
                src={organisation.logoUrl}
                sx={{ width: avatarSize, height: avatarSize, fontWeight: 800, background: organisation.logoUrl ? undefined : brandGradient }}
            >
                {initials(organisation.companyName)}
            </Avatar>
        );

        const nameBlock = (
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                {avatar}
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{organisation.companyName}</Typography>
                    {subtitle && <Typography variant="caption" color="text.secondary" noWrap display="block">{subtitle}</Typography>}
                </Box>
            </Stack>
        );

        return (
            <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="space-between" sx={sx}>
                {onClick ? (
                    <ButtonBase onClick={onClick} sx={{ borderRadius: 1, p: 0.25, justifyContent: 'flex-start', textAlign: 'left', minWidth: 0, flex: 1 }}>
                        {nameBlock}
                    </ButtonBase>
                ) : nameBlock}
                {action}
            </Stack>
        );
    }

    if (variant === 'row') {
        return (
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
                useFlexGap
                sx={[{ py: 1.5, px: 1, borderRadius: 1.5 }, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
            >
                <ButtonBase
                    onClick={onClick}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flexGrow: 1, borderRadius: 1, justifyContent: 'flex-start', textAlign: 'left' }}
                >
                    <Avatar variant="rounded" src={organisation.logoUrl} sx={{ width: 48, height: 48, background: organisation.logoUrl ? undefined : brandGradient, color: theme.palette.getContrastText(ownerPrimary) }}>
                        {initials(organisation.companyName)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="subtitle1" noWrap sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' }, overflowWrap: 'anywhere' }}>{organisation.companyName}</Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {subtitle ?? defaultSubtitle(organisation, true)}
                        </Typography>
                    </Box>
                </ButtonBase>
                {!action && organisation.nextEventStartDate && (
                    <Chip label="Upcoming events" size="small" sx={{ display: { xs: 'none', sm: 'inline-flex' }, bgcolor: 'success.lighter', color: 'success.dark' }} />
                )}
                {action ?? <Button variant="outlined" size="small" onClick={onClick} sx={{ borderRadius: 999, minWidth: 96 }}>View page</Button>}
            </Stack>
        );
    }

    return (
        <Card
            variant="outlined"
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick?.();
                }
            }}
            sx={[
                {
                    height: '100%',
                    minHeight: 176,
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s, transform 0.15s',
                    '&:hover': { boxShadow: theme.shadows[3], transform: 'translateY(-1px)' },
                    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ] as SxProps<Theme>}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar variant="rounded" src={organisation.logoUrl} sx={{ width: 48, height: 48, background: organisation.logoUrl ? undefined : brandGradient, color: theme.palette.getContrastText(ownerPrimary) }}>
                        {initials(organisation.companyName)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap>{organisation.companyName}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {organisation.eventCount} event{organisation.eventCount === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 1.5 }}>
                    {subtitle ?? defaultSubtitle(organisation, false)}
                </Typography>
                <Box sx={{ mt: 1.5 }} onClick={(event) => event.stopPropagation()}>
                    {action ?? (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={(event) => { event.stopPropagation(); onClick?.(); }}
                            sx={{ borderRadius: 999, minWidth: 96 }}
                        >
                            View page
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

/** Row-style organiser summary — thin preset over OrganisationCard for list contexts. */
export const OrganisationRow: React.FC<Omit<OrganisationRowProps, 'variant'>> = (props) => (
    <OrganisationCard {...props} variant="row" />
);

/** Placeholder matching OrganisationRow's shape, shown while the organisations list is loading. */
export const OrganisationRowSkeleton: React.FC = () => (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1.5 }}>
        <Skeleton variant="rounded" width={48} height={48} />
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Skeleton variant="text" width="45%" sx={{ fontSize: '1rem' }} />
            <Skeleton variant="text" width="70%" sx={{ fontSize: '0.85rem' }} />
        </Box>
        <Skeleton variant="rounded" width={96} height={32} sx={{ borderRadius: 999 }} />
    </Stack>
);

export const OrganisationCardSkeleton: React.FC = () => (
    <Card variant="outlined" sx={{ height: '100%', minHeight: 176, borderRadius: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Skeleton variant="rounded" width={48} height={48} />
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Skeleton variant="text" width="60%" sx={{ fontSize: '1rem' }} />
                    <Skeleton variant="text" width="40%" sx={{ fontSize: '0.75rem' }} />
                </Box>
            </Stack>
            <Skeleton variant="text" width="80%" sx={{ mt: 1.5 }} />
            <Skeleton variant="rounded" width={96} height={32} sx={{ mt: 1.5, borderRadius: 999 }} />
        </CardContent>
    </Card>
);
